/**
 * アニマルID(共通認証)でのログイン
 *
 * ログイン画面からアニマルID の認可エンドポイントへ送り出し、戻り先(/auth/callback)で受け取った認可コードを
 * API(/api/v2/edi/token/oidc)へ送って EDI のトークンを受け取る。認可コードの交換と ID トークンの検証は API が行う。
 * 送り出すときに作った state・nonce・code_verifier は、戻ってくるまでそのタブの sessionStorage に置く。
 */
import { ediTokenObtainOidc } from '@/api/hanly-v2/edi/edi';
import { ANIMAL_ID_ERROR_MESSAGE, parseAnimalIdSignInError } from '@/features/auth/sign-in-error';
import { saveTokens } from '@/libs/auth-storage';
import { SIGNIN_PATH } from '@/utils/return-to';

// アニマルID に登録した戻り先。API が認可コードを交換するときに渡す戻り先(EDI_UI_BASE_URL の /auth/callback)と同じにする
export const CALLBACK_PATH = '/auth/callback';
const PENDING_SIGN_IN_KEY = 'animalIdSignIn';
// API は ID トークンの sub で利用者を決めるので、ほかの利用者の情報は求めない
const SCOPE = 'openid';

export type AnimalIdConfig = { issuer: string; clientId: string };

/** 送り出したときに作り、戻ってきたときに照合・送信する値 */
type PendingSignIn = { state: string; nonce: string; codeVerifier: string; returnTo: string | null };

export type AnimalIdSignInResult =
  | { type: 'success'; returnTo: string | null }
  | { type: 'error'; message: string; returnTo: string | null };

/** アニマルID でのログインの設定。設定していない環境では null(ログイン画面にボタンを出さない) */
export const getAnimalIdConfig = (): AnimalIdConfig | null => {
  const issuer: string | undefined = import.meta.env.VITE_ANIMAL_ID_ISSUER;
  const clientId: string | undefined = import.meta.env.VITE_ANIMAL_ID_CLIENT_ID;
  return issuer && clientId ? { issuer, clientId } : null;
};

const toBase64Url = (bytes: Uint8Array) =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

// 推測できない 256 bit の値。code_verifier に使うと、RFC 7636 の 43〜128 文字に収まる 43 文字になる
const randomValue = () => toBase64Url(crypto.getRandomValues(new Uint8Array(32)));

const codeChallengeOf = async (codeVerifier: string) =>
  toBase64Url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier))));

/** アニマルID のログイン画面へ送り出す URL を作り、戻ってきたときに照合する値を保存する */
export const startAnimalIdSignIn = async (
  config: AnimalIdConfig,
  returnTo: string | null,
): Promise<string> => {
  const pending: PendingSignIn = {
    state: randomValue(),
    nonce: randomValue(),
    codeVerifier: randomValue(),
    returnTo,
  };
  const url = new URL('/authorize/', config.issuer);
  url.search = new URLSearchParams({
    response_type: 'code',
    client_id: config.clientId,
    redirect_uri: new URL(CALLBACK_PATH, window.location.origin).href,
    scope: SCOPE,
    state: pending.state,
    nonce: pending.nonce,
    code_challenge: await codeChallengeOf(pending.codeVerifier),
    code_challenge_method: 'S256',
  }).toString();
  sessionStorage.setItem(PENDING_SIGN_IN_KEY, JSON.stringify(pending));
  return url.href;
};

/** 送り出したときに保存した値を取り出す。state が合うときだけ返し、同じ値を 2 度使わないよう消す */
const takePendingSignIn = (state: string | null): PendingSignIn | null => {
  const saved = sessionStorage.getItem(PENDING_SIGN_IN_KEY);
  if (!saved || !state) return null;
  let pending: PendingSignIn;
  try {
    pending = JSON.parse(saved) as PendingSignIn;
  } catch {
    return null;
  }
  // 合わない state は、このタブで始めたログインの戻りではない(別のタブ・古いリンク・攻撃者が用意したリンク)
  if (pending.state !== state) return null;
  sessionStorage.removeItem(PENDING_SIGN_IN_KEY);
  return pending;
};

/** アニマルID から戻り先に渡された値で EDI にログインし、トークンを保存する */
export const completeAnimalIdSignIn = async (params: URLSearchParams): Promise<AnimalIdSignInResult> => {
  const pending = takePendingSignIn(params.get('state'));
  if (!pending) {
    return {
      type: 'error',
      message: 'ログインを始めた記録が見つかりませんでした。もう一度ログインしてください。',
      returnTo: null,
    };
  }
  const code = params.get('code');
  // 認可コードの代わりに error が返された(ログインの取り消し・連携サービスの設定の誤りなど)
  if (!code) {
    return { type: 'error', message: ANIMAL_ID_ERROR_MESSAGE, returnTo: pending.returnTo };
  }
  try {
    const tokens = await ediTokenObtainOidc({
      code,
      code_verifier: pending.codeVerifier,
      nonce: pending.nonce,
    });
    saveTokens({ access: tokens.access, refresh: tokens.refresh, idToken: tokens.id_token });
    return { type: 'success', returnTo: pending.returnTo };
  } catch (error) {
    return { type: 'error', message: await parseAnimalIdSignInError(error), returnTo: pending.returnTo };
  }
};

/** アニマルID からもログアウトする URL。アニマルID のセッションを終え、EDI のログイン画面へ戻す */
export const buildAnimalIdLogoutUrl = (config: AnimalIdConfig, idToken: string): string => {
  const url = new URL('/logout/', config.issuer);
  url.search = new URLSearchParams({
    id_token_hint: idToken,
    post_logout_redirect_uri: new URL(SIGNIN_PATH, window.location.origin).href,
  }).toString();
  return url.href;
};
